import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { ProductItem } from "@project/product-item";
import { Expose, Transform, Type } from "class-transformer";
import { IsArray, IsOptional } from "class-validator";

export class Regard3000Configuration {
  @IsArray()
  @Expose({ name: "baseUnits" })
  @Type(() => ProductItem)
  @Transform(({ value, obj }) => {
    // migrate baseUnit for backward compatibility
    if (Array.isArray(value)) return value;
    const legacy = obj?.baseUnit;
    if (typeof legacy === "string" && legacy.trim().length > 0) {
      return [ProductItem.create(legacy)];
    }
    return [];
  })
  private readonly _baseUnits: ProductItem[];

  @IsArray()
  @IsOptional()
  @Type(() => ProductItem)
  @Expose({ name: "inputModules" })
  @Transform(({ value, obj }) => {
    // migrate inputModule for backward compatibility
    if (Array.isArray(value)) return value;
    const legacy = obj?.inputModule;
    if (typeof legacy === "string" && legacy.trim().length > 0) {
      return [ProductItem.create(legacy)];
    }
    return [];
  })
  private readonly _inputModules?: ProductItem[];

  @IsArray()
  @IsOptional()
  @Type(() => ProductItem)
  @Expose({ name: "relayModules" })
  @Transform(({ value, obj }) => {
    // migrate relayModule for backward compatibility
    if (Array.isArray(value)) return value;
    const legacy = obj?.relayModule;
    if (typeof legacy === "string" && legacy.trim().length > 0) {
      return [ProductItem.create(legacy)];
    }
    return [];
  })
  private readonly _relayModules?: ProductItem[];

  @IsArray()
  @IsOptional()
  @Type(() => ProductItem)
  @Expose({ name: "gatewayModules" })
  @Transform(({ value, obj }) => {
    // migrate gatewayModule for backward compatibility
    if (Array.isArray(value)) return value;
    const legacy = obj?.gatewayModule;
    if (typeof legacy === "string" && legacy.trim().length > 0) {
      return [ProductItem.create(legacy)];
    }
    return [];
  })
  private readonly _gatewayModules?: ProductItem[];

  @IsOptional()
  @Type(() => ProductItem)
  @Expose({ name: "slotCovers" })
  @Transform(({ value, obj }) => {
    // in case slotCovers are undefined verify configuration and fix it if broken
    // slotCovers === undefined is a valid usecase
    if (!value) {
      const slotCoverProductId = "3705672";
      const baseUnits = (obj?.baseUnits ?? []) as Array<{ quantity?: number }>;
      const inputModules = (obj?.inputModules ?? []) as Array<{ quantity?: number }>;
      const relayModules = (obj?.relayModules ?? []) as Array<{ quantity?: number }>;
      const gatewayModules = (obj?.gatewayModules ?? []) as Array<{ quantity?: number }>;

      const baseUnitCount = Array.isArray(baseUnits)
        ? baseUnits.reduce((sum, { quantity = 0 }) => sum + Number(quantity || 0), 0)
        : 0;

      const inputModuleCount = Array.isArray(inputModules)
        ? inputModules.reduce((sum, { quantity = 0 }) => sum + Number(quantity || 0), 0)
        : 0;

      const relayModuleCount = Array.isArray(relayModules)
        ? relayModules.reduce((sum, { quantity = 0 }) => sum + Number(quantity || 0), 0)
        : 0;

      const gatewayModuleCount = Array.isArray(gatewayModules)
        ? gatewayModules.reduce((sum, { quantity = 0 }) => sum + Number(quantity || 0), 0)
        : 0;

      const totalSlotCount = baseUnitCount * 3;
      const assignedSlotCount = inputModuleCount + relayModuleCount + gatewayModuleCount;
      const openSlotCount = totalSlotCount - assignedSlotCount;
      // if openSlotCount is > 0 then the configuration is broken and must be fixed
      if (openSlotCount > 0) {
        value = new ProductItem(slotCoverProductId, openSlotCount);
      }
    }

    if (value instanceof ProductItem) {
      return value;
    }
    // migrate slotcovers for backward compatibility
    const legacyArr = Array.isArray(value) ? value : Array.isArray(obj?.slotCovers) ? obj.slotCovers : undefined;

    if (Array.isArray(legacyArr) && legacyArr.every((v) => typeof v === "string")) {
      if (legacyArr.length === 0) return undefined;
      const id = legacyArr[0];
      const quantity = legacyArr.length;
      return ProductItem.create(id, quantity);
    }
    return undefined;
  })
  private readonly _slotCovers?: ProductItem;

  @IsArray()
  @Type(() => ProductItem)
  @Expose({ name: "lidUnits" })
  @Transform(({ value, obj }) => {
    // migrate lidUnit for backward compatibility
    if (Array.isArray(value)) return value;
    const legacy = obj?.lidUnit;
    if (typeof legacy === "string" && legacy.trim().length > 0) {
      return [ProductItem.create(legacy)];
    }
    return [];
  })
  private readonly _lidUnits: ProductItem[];

  constructor(
    baseUnits: ProductItem[],
    lidUnits: ProductItem[],
    inputModules?: ProductItem[],
    relayModules?: ProductItem[],
    gatewayModules?: ProductItem[],
    slotCovers?: ProductItem,
  ) {
    this._baseUnits = baseUnits;
    this._lidUnits = lidUnits;
    this._inputModules = inputModules;
    this._relayModules = relayModules;
    this._gatewayModules = gatewayModules;
    this._slotCovers = slotCovers;
  }

  get baseUnits(): ProductItem[] {
    return this._baseUnits;
  }

  get lidUnits(): ProductItem[] {
    return this._lidUnits;
  }

  get inputModules(): ProductItem[] | undefined {
    return this._inputModules;
  }

  get relayModules(): ProductItem[] | undefined {
    return this._relayModules;
  }

  get gatewayModules(): ProductItem[] | undefined {
    return this._gatewayModules;
  }

  get slotCovers(): ProductItem | undefined {
    return this._slotCovers;
  }

  static create(
    selectedInputModules: ProductWithQuantity[],
    selectedRelayModules: ProductWithQuantity[],
    selectedGatewayModules: ProductWithQuantity[],
    selectedBaseUnits: ProductWithQuantity[],
    selectedLidUnits: ProductWithQuantity[],
    calculatedSlotCovers?: ProductWithQuantity,
  ) {
    const inputModules: ProductItem[] = [];
    const relayModules: ProductItem[] = [];
    const gatewayModules: ProductItem[] = [];
    const baseUnits: ProductItem[] = [];
    const lidUnits: ProductItem[] = [];
    let slotCovers: ProductItem | undefined = undefined;
    if (calculatedSlotCovers) {
      slotCovers = ProductItem.create(calculatedSlotCovers.product.id, calculatedSlotCovers.quantity);
    }

    for (const module of selectedInputModules) {
      inputModules.push(ProductItem.create(module.product.id, module.quantity));
    }

    for (const module of selectedRelayModules) {
      relayModules.push(ProductItem.create(module.product.id, module.quantity));
    }

    for (const module of selectedGatewayModules) {
      gatewayModules.push(ProductItem.create(module.product.id, module.quantity));
    }

    for (const baseUnit of selectedBaseUnits) {
      baseUnits.push(ProductItem.create(baseUnit.product.id, baseUnit.quantity));
    }
    for (const lidUnit of selectedLidUnits) {
      lidUnits.push(ProductItem.create(lidUnit.product.id, lidUnit.quantity));
    }
    return new Regard3000Configuration(baseUnits, lidUnits, inputModules, relayModules, gatewayModules, slotCovers);
  }
}
